// src/components/CommunicationsHub.jsx
// Dashboard widget - unified conversation list with filter tabs

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Mail, Phone, ChevronRight, Inbox,
  ExternalLink, Loader2
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Filter tabs
const FILTER_TABS = [
  { id: 'all', label: 'All', icon: Inbox, badgeKey: 'total' },
  { id: 'sms', label: 'Messages', icon: MessageSquare, badgeKey: 'sms' },
  { id: 'email', label: 'Email', icon: Mail, badgeKey: 'email' },
  { id: 'call', label: 'Calls', icon: Phone, badgeKey: 'call' },
];

// Type icon mapping
const getTypeIcon = (type) => {
  switch (type) {
    case 'sms': return MessageSquare;
    case 'email': return Mail;
    case 'call': return Phone;
    default: return MessageSquare;
  }
};

const getTypeColor = (type) => {
  switch (type) {
    case 'sms': return 'text-blue-500';
    case 'email': return 'text-red-500';
    case 'call': return 'text-green-500';
    default: return 'text-gray-500';
  }
};

// Format relative time
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

// Get preview text for a conversation
const getPreview = (conversation) => {
  const msg = conversation.lastMessage;
  if (!msg) return 'No messages';
  const prefix = msg.direction === 'outbound' ? 'You: ' : '';
  if (msg.type === 'call') {
    const status = msg.metadata?.status || 'completed';
    const isMissed = status === 'no-answer' || status === 'busy' || status === 'canceled';
    return isMissed ? 'Missed call' : (msg.direction === 'outbound' ? 'Outgoing call' : 'Incoming call');
  }
  if (msg.type === 'email') {
    return prefix + (msg.subject || '(No subject)');
  }
  return prefix + (msg.body || 'No message');
};

const CommunicationsHub = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({ sms: 0, email: 0, call: 0, total: 0 });
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupedRes, unreadRes] = await Promise.all([
          fetch(`${API_BASE}/comms/grouped`, { credentials: 'include' }),
          fetch(`${API_BASE}/comms/unread-counts`, { credentials: 'include' })
        ]);

        if (groupedRes.ok) {
          const data = await groupedRes.json();
          setConversations(data.data || []);
        }
        if (unreadRes.ok) {
          const data = await unreadRes.json();
          setUnreadCounts(data.data || { sms: 0, email: 0, call: 0, total: 0 });
        }
      } catch (error) {
        console.error('Error fetching communications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter and sort conversations
  const filteredConversations = useMemo(() => {
    let filtered = [...conversations];

    if (activeFilter !== 'all') {
      filtered = filtered.filter(c => c.lastMessage?.type === activeFilter);
    }

    // Sort: unread first, then by most recent
    filtered.sort((a, b) => {
      if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
      if (b.unreadCount > 0 && a.unreadCount === 0) return 1;
      const aTime = new Date(a.lastMessage?.timestamp || a.lastMessageTime || 0);
      const bTime = new Date(b.lastMessage?.timestamp || b.lastMessageTime || 0);
      return bTime - aTime;
    });

    return filtered.slice(0, 12);
  }, [conversations, activeFilter]);

  const handleClickConversation = (clientId) => {
    navigate(`/communications?clientId=${clientId}`);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-amber-500" />
          <h3 className="font-semibold text-gray-900">Communications</h3>
          {unreadCounts.total > 0 && (
            <span className="min-w-[20px] h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center font-bold px-1.5">
              {unreadCounts.total > 99 ? '99+' : unreadCounts.total}
            </span>
          )}
        </div>
        <button
          onClick={() => navigate('/communications')}
          className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium transition-colors"
        >
          View All
          <ExternalLink className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-gray-100 bg-gray-50/50">
        {FILTER_TABS.map((tab) => {
          const Icon = tab.icon;
          const count = tab.badgeKey ? unreadCounts[tab.badgeKey] : null;
          const isActive = activeFilter === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
              {count > 0 && (
                <span className={`min-w-[16px] h-4 text-[10px] rounded-full flex items-center justify-center px-1 ${
                  isActive ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Conversation List */}
      <div className="overflow-y-auto max-h-[420px]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="py-10 text-center text-gray-400">
            <Inbox className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm font-medium">No conversations</p>
            <p className="text-xs mt-1">
              {activeFilter !== 'all' ? 'Try a different filter' : 'No recent activity'}
            </p>
          </div>
        ) : (
          filteredConversations.map((conversation) => {
            const hasUnread = conversation.unreadCount > 0;
            const TypeIcon = getTypeIcon(conversation.lastMessage?.type);
            const typeColor = getTypeColor(conversation.lastMessage?.type);
            const initials = getInitials(conversation.clientName);
            const preview = getPreview(conversation);
            const time = formatTime(conversation.lastMessage?.timestamp || conversation.lastMessageTime);

            return (
              <div
                key={conversation.clientId}
                onClick={() => handleClickConversation(conversation.clientId)}
                className={`flex items-center gap-3 px-3 py-2.5 border-b border-gray-50 cursor-pointer transition-colors ${
                  hasUnread ? 'bg-amber-50/40 hover:bg-amber-50' : 'hover:bg-gray-50'
                }`}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-white font-bold text-sm">
                    {initials}
                  </div>
                  {hasUnread && (
                    <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-amber-500 rounded-full border-2 border-white" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                      {conversation.clientName || 'Unknown'}
                    </span>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">{time}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <TypeIcon className={`w-3 h-3 flex-shrink-0 ${typeColor}`} />
                    <span className={`text-xs truncate ${hasUnread ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                      {preview}
                    </span>
                  </div>
                </div>

                {/* Unread count badge */}
                {conversation.unreadCount > 1 && (
                  <span className="min-w-[18px] h-[18px] bg-amber-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold px-1 flex-shrink-0">
                    {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                  </span>
                )}

                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CommunicationsHub;
